import { FC, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Modal } from '@savgroup-front-common/core/atoms/modal';
import SwipeableDrawer, {
  DRAWER_SIZE,
} from '@savgroup-front-common/core/atoms/SwipeableDrawer/SwipeableDrawer';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { SUPPORTED_ICON_POSITIONS } from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { ICONS_TYPE, PickupPoint } from '@savgroup-front-common/types';
import useGetPickupPoints from '@/view/app/hooks/useGetPickupPoints';
import { PageFooterContainer } from '@/view/app/NewLayout/NewStepLayout';
import { NewStepLayoutProfessional } from '@/view/app/NewLayout/NewStepLayout/NewStepLayoutProfessional';
import {
  PROFESSIONAL_STEP_CONFIG,
  PROFESSIONAL_STEP_PROGRESS,
} from '@/view/app/NewLayout/ProfessionalRoutes.config';
import { buildProfessionalRoute } from '@/view/app/NewLayout/ProfessionalRoutes.helpers';

import { AddressManagement } from '../../../../../components/AddressManagement';
import HandlingCard from '../../../../../components/HandlingCard';
import PickupPointSelector from '../../../../../components/PickupPointSelector';
import { ResumeSolution } from '../../../../../components/ResumeSolution';
import { ResumeCard } from '../../../components/ResumeCard';
import { IrshStepValues } from '../../IrshPages.types';
import useClaimGroupDepositPage from '../ClaimGroupDepositPage.hooks';
import {
  $DepositItemWrapper,
  $DepositResultWrapper,
  $DepositWrapper,
  $Heading,
  $HeadingIcon,
  $LeftColumn,
} from '../ClaimGroupDepositPage.styles';
import messages from '../messages';

interface ClaimGroupDepositProps {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
  onPreviousStep: RoutedStepProps<IrshStepValues>['onPreviousStep'];
}

export const ClaimGroupDeposit: FC<ClaimGroupDepositProps> = ({
  onNextStep,
  onPreviousStep,
}) => {
  const claimGroupId = useParams().claimGroupId as string;

  const [openResumeModal, setOpenResumeModal] = useState(false);

  const {
    claimGroupConfirmation,
    groupedCarriers,
    formContext,
    changeSelection,
    onSubmit,
    handling,
    isDepositHandlingLoading,
    chooseHandlingSelected,
    handlingSelected,
    homeSelectedAddressChanged,
    weight,
  } = useClaimGroupDepositPage({
    onNextStep,
  });

  const {
    setValue,
    formState: { isValid, isSubmitting },
  } = formContext;

  const {
    sellerId,
    carriers,
    addressSelected,
    pickupPoints,
    noPickupPoints,
    addresses,
    handleSetAddressSelected,
    selectedPickupPoint,
    pickupPointsIsLoading,
  } = useGetPickupPoints({
    selectedCarrierType: chooseHandlingSelected,
    groupedCarriers,
    claimGroupId,
    weight,
  });

  const handleSetSelectedPickupPoint = async (
    pickupPointSelected?: PickupPoint,
  ) => {
    if (!pickupPointSelected) {
      return;
    }

    setValue('pickupPointSelected', pickupPointSelected);

    await onSubmit();
  };

  const handleOpenModal = () => setOpenResumeModal((prev) => !prev);

  return (
    <>
      <FormProvider {...formContext}>
        <form onSubmit={onSubmit}>
          <NewStepLayoutProfessional
            stepName={PROFESSIONAL_STEP_CONFIG.PROFESSIONAL_DEPOSIT_PAGE}
            customSteps={PROFESSIONAL_STEP_PROGRESS}
          >
            <$DepositWrapper>
              <$LeftColumn>
                <ResumeSolution
                  claimGroupId={claimGroupId}
                  claimGroupConfirmation={claimGroupConfirmation}
                  customBuildUpdateSolutionUrl={buildProfessionalRoute({
                    route: PROFESSIONAL_STEP_CONFIG.PROFESSIONAL_SOLUTION_PAGE,
                    payload: { claimGroupId },
                  })}
                />
                <$Heading>
                  <$HeadingIcon>
                    <MyAccountIcon icon={ICONS_TYPE.DEPOSIT_ICON} />
                  </$HeadingIcon>
                  <SafeFormattedMessageWithoutSpread
                    message={
                      handling?.hasHome
                        ? messages.handlingTitleOnlyHome
                        : messages.handlingTitle
                    }
                  />
                </$Heading>
                <$DepositItemWrapper>
                  {isDepositHandlingLoading && <BaseLoader />}
                  {!isDepositHandlingLoading && (
                    <HandlingCard groupedCarriers={groupedCarriers} />
                  )}
                </$DepositItemWrapper>
              </$LeftColumn>

              <$DepositResultWrapper>
                <ResumeCard />
              </$DepositResultWrapper>
            </$DepositWrapper>

            <SwipeableDrawer
              isOpen={handlingSelected}
              onClose={changeSelection}
              hasBackdrop
              hasPadding={false}
              hasBackdropScroll={false}
            >
              {handlingSelected && (
                <PickupPointSelector
                  maxDistance={200}
                  pickupPoints={pickupPoints}
                  selectedPickupPointId={
                    selectedPickupPoint ? selectedPickupPoint.id : undefined
                  }
                  onSelectPickupPoint={handleSetSelectedPickupPoint}
                  searchAddress={addressSelected}
                  userAddresses={addresses}
                  onAddressSelected={handleSetAddressSelected}
                  carriers={carriers}
                  pickupPointsIsLoading={pickupPointsIsLoading}
                  sellerId={sellerId}
                  noPickupPoints={noPickupPoints}
                />
              )}
            </SwipeableDrawer>

            <SwipeableDrawer
              isOpen={homeSelectedAddressChanged}
              size={DRAWER_SIZE.SMALL}
              onClose={changeSelection}
              hasBackdrop
              hasBackdropScroll={false}
            >
              <AddressManagement onSubmit={onSubmit} />
            </SwipeableDrawer>
          </NewStepLayoutProfessional>

          <PageFooterContainer>
            <Button
              primary
              type={BUTTON_TYPES.BUTTON}
              icon={<ArrowNakedRightIcon />}
              position={SUPPORTED_ICON_POSITIONS.RIGHT}
              disabled={!isValid}
              isLoading={isSubmitting}
              onClick={onSubmit}
              dataTestId="nextButton"
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.nextButton}
              />
            </Button>

            <Button
              type={BUTTON_TYPES.BUTTON}
              tertiary
              onClick={() => {
                onPreviousStep({});
              }}
              icon={<ArrowNakedLeftIcon />}
              position={SUPPORTED_ICON_POSITIONS.LEFT}
              dataTestId="previousButton"
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.previousButton}
              />
            </Button>
          </PageFooterContainer>
        </form>
      </FormProvider>
      <Modal
        isOpen={openResumeModal}
        onClose={handleOpenModal}
        shouldShowPadding={false}
      >
        <ResumeCard />
      </Modal>
    </>
  );
};
